import classNames from 'classnames';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styles from './quiz-number-field.module.scss';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import {
    faInfoCircle as faInfoCircleSolid,
    faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import MoshModal from 'src/components/MoshModal';
import HTMLReactParser from 'html-react-parser';
import { env, isMoshy } from 'src/env.mjs';

const QUIZ_NUMBER_FIELD_MIN = 20;
const QUIZ_NUMBER_FIELD_MAX = 300;

const QuizNumberField = () => {
    const {
        currentQuestion,
        handleSubmitAnswer,
        prefillQuestionIds,
        submittedQuestionIds,
        inQueue,
        state,
    } = useContext(QuizContext);
    const {
        questionId,
        questionText,
        questionExplanation,
        questionImage,
        answerList,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        medication,
        additionalField1: modalHeading,
        additionalField2: modalDescription,
        additionalField3: minValue,
        additionalField4: maxValue,
        estimateTimeText,
        largeImage,
    } = currentQuestion || {};
    const [questionStart = '', questionEnd = ''] = questionText.split(' _ ');
    const { frontendStamp: answerId } = answerList[0] || {};
    const [pageTransitioning] = state.pageTransitioning;
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [textInputValue, setTextInputValue] = useState('');
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [inputError, setInputError] = useState(false);
    const [inputTouched, setInputTouched] = useState(false);
    const parsedMinValue = minValue
        ? parseInt(minValue, 10)
        : QUIZ_NUMBER_FIELD_MIN;
    const parsedMaxValue = maxValue
        ? parseInt(maxValue, 10)
        : QUIZ_NUMBER_FIELD_MAX;
    const errorText = estimateTimeText || 'Please review your answer';

    const isInvalidInput = (value: string, min: number, max: number) => {
        if (value.length === 0) return true;
        const parsedValue = parseInt(value);
        return parsedValue < min || parsedValue > max;
    };

    // Automatically focus input
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (inputRef.current) inputRef.current?.focus();
    }, []);
    // Handle input change
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const textValue = event.target?.value;
        setTextInputValue(textValue);
    };
    // Handle submitting
    const handleSubmit = useCallback(() => {
        if (textInputValue.length > 0 && !continueDisabled && questionId) {
            handleSubmitAnswer(
                {
                    questionId: questionId,
                    questionText: questionText,
                    answerIds: answerId ? [answerId] : undefined,
                    answerTexts: [textInputValue],
                    medication: medication ? medication : '',
                },
                () => {
                    setTextInputValue('');
                },
            );
        }
    }, [
        continueDisabled,
        textInputValue,
        questionId,
        questionText,
        answerId,
        medication,
        handleSubmitAnswer,
    ]);
    // Set existing answer
    useEffect(() => {
        let newAnswerText: string | null = null;
        if (
            questionId &&
            submittedQuestionIds.includes(questionId) &&
            userAnswers.length > 0
        ) {
            newAnswerText = userAnswers[0].answerText;
        } else if (
            inQueue &&
            prefillNeeded &&
            prefillQuestionIds.includes(questionId) &&
            prefillAnswers.length > 0
        ) {
            newAnswerText = prefillAnswers[0].answerText;
        }
        if (newAnswerText) {
            setTextInputValue(newAnswerText);
        }
    }, [
        questionId,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        prefillQuestionIds,
        submittedQuestionIds,
        inQueue,
    ]);
    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleSubmit);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleSubmit,
            );
    }, [handleSubmit]);
    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter') handleSubmit();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleSubmit]);
    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            setContinueDisabled(
                textInputValue === null ||
                    isInvalidInput(
                        textInputValue,
                        parsedMinValue,
                        parsedMaxValue,
                    ),
            );
        }
    }, [textInputValue, pageTransitioning, inputError, setContinueDisabled]);
    // Error state
    useEffect(() => {
        if (!pageTransitioning && inputTouched) {
            setInputError(
                isInvalidInput(textInputValue, parsedMinValue, parsedMaxValue),
            );
        }
    }, [textInputValue, pageTransitioning, inputTouched]);

    return (
        <div className={classNames(styles['quiz-number-field--wrapper'])}>
            {/* Content header */}
            {questionImage && largeImage ? (
                <div className={classNames(styles['large-image-container'])}>
                    <img
                        className={classNames(styles['large-image'])}
                        src={questionImage}
                        alt={questionText}
                    />
                </div>
            ) : questionImage ? (
                <div className={classNames(styles['quiz-field-header'])}>
                    <img
                        className={classNames(styles['quiz-field-icon'])}
                        src={questionImage}
                        alt={questionText}
                    />
                </div>
            ) : null}

            <div className={classNames(styles['quiz-field-content'])}>
                <div className={classNames(styles['question'])}>
                    <h4 className={classNames(styles['question--start'])}>
                        {questionStart}
                    </h4>
                    <input
                        data-testid="number-input"
                        ref={inputRef}
                        className={classNames(styles['question--input'], 'h4')}
                        type="number"
                        value={textInputValue}
                        onChange={handleChange}
                        autoFocus
                        min={parsedMinValue}
                        max={parsedMaxValue}
                        onFocus={() => {
                            setInputTouched(false);
                            setInputError(false);
                        }}
                        onBlur={() => setInputTouched(!!textInputValue)}
                    />
                    <h4 className={classNames(styles['question--end'])}>
                        {questionEnd}
                    </h4>
                </div>
                {/* Explanation */}
                {questionExplanation ? (
                    <h5 className={classNames(styles['explanation'], 'pt-2')}>
                        {questionExplanation}
                    </h5>
                ) : null}
                {modalHeading ? (
                    <button
                        className={classNames(
                            styles['quiz-info--wrapper'],
                            'flex items-center gap-x-2',
                            !(modalHeading && modalDescription)
                                ? 'cursor-default'
                                : '',
                        )}
                        onClick={() => {
                            if (modalHeading && modalDescription) {
                                setIsModalOpen(true);
                            }
                        }}
                    >
                        {modalHeading && modalDescription ? (
                            <span className="h-6 w-6">
                                <FontAwesomeIcon
                                    icon={
                                        isMoshy
                                            ? faInfoCircle
                                            : faInfoCircleSolid
                                    }
                                    className="mr-2 !h-full w-full"
                                />
                            </span>
                        ) : null}
                        <h5
                            className={classNames(
                                styles['quiz-info--message'],
                                'highlight',
                            )}
                        >
                            {modalHeading}
                        </h5>
                    </button>
                ) : null}
                <MoshModal
                    open={isModalOpen}
                    ariaLabel={modalHeading}
                    onModalClose={(value) => setIsModalOpen(value || false)}
                    custom={true}
                    theme={`theme-${env.NEXT_PUBLIC_BRAND}`}
                >
                    <div>
                        <h4 className="first-letter:capitalize">
                            {modalHeading}
                        </h4>
                        <div className="mb-0 mt-2">
                            {modalDescription
                                ? HTMLReactParser(modalDescription.toString())
                                : null}
                        </div>
                    </div>
                </MoshModal>
                {inputError && (
                    <p
                        className={classNames(
                            styles['text'],
                            styles['error-message'],
                        )}
                    >
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        {errorText}
                    </p>
                )}
            </div>
        </div>
    );
};

export default QuizNumberField;
